import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

// Design System
import { Message } from 'jl-design-system/components/message/Message';
import { Modal } from 'jl-design-system/components/modal/Modal';

// Types
import { AppDispatch } from 'types/RootState.types';
import { ThreeDSecureProps } from './ThreeDSecure.types';

// Config
import threeDSecureState from './ThreeDSecure.state';
import { ANALYTICS_PAYMENT_SHOW_3D_SECURE } from '@constants/actionConstants';
import appConstants from '@constants/appConstants';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import { disabledEscKeyHandler } from '@utils/helpers/eventKeyActions';
import { removeWhitespace } from '@utils/helpers/stringUtils';

// Components
import BodyText from '@components/body-text';
import Container from '@components/container';
import Iframe from '@widgets/iframe';

// Styles
import styles from './three-d-secure.scss';

const ThreeDSecure = ({ showCallCentreNumberOn3DSError }: ThreeDSecureProps) => {
  const dispatch: AppDispatch = useDispatch();
  const { action, id, inputData } = useSelector(threeDSecureState);

  const hiddenForm = useRef<HTMLFormElement>(null);
  const [frameLoaded, setFrameLoaded] = useState<boolean>(false);

  useEffect(() => {
    dispatch(triggerAnalyticsEvent(ANALYTICS_PAYMENT_SHOW_3D_SECURE));

    setTimeout(() => {
      if (hiddenForm?.current?.submit) {
        hiddenForm.current.submit();
      }
    }, 0);

    document.addEventListener('keydown', disabledEscKeyHandler, true);

    return () => {
      document.removeEventListener('keydown', disabledEscKeyHandler, true);
    };
  }, []);

  const onLoadHandler = () => setFrameLoaded(true);

  const renderPhoneNumber = () => {
    const phoneNumbers = appConstants.CALL_CENTRE_NUMBERS;

    let tel = removeWhitespace(phoneNumbers.standard);
    let telDisplay = phoneNumbers.standard;

    // this feature is controlled in monetate so it's only enabled during call centre hours
    if (showCallCentreNumberOn3DSError) {
      tel = removeWhitespace(phoneNumbers.enhanced);
      telDisplay = phoneNumbers.enhanced;
    }

    return (
      <Container marginTop="2" textAlign="center" width="full">
        <BodyText testId="call-centre-message">
          For any other issues, please call John Lewis on <a href={`tel:${tel}`}>{telDisplay}</a>
          <br />
          to complete your order.
        </BodyText>
      </Container>
    );
  };

  const renderContent = () => (
    <Container
      flex={{
        alignItems: 'center',
        flexDirection: 'col',
        flexItem: 'auto',
      }}
      height="full"
      testId="payment-authentication-container"
      width="full"
    >
      <Container marginBottom="2">
        <Message
          body="Your bank may send a one-off code via text, email or its app to confirm payment. If you haven't received a code, please call your bank on the phone number from the back of your debit or credit card."
          title="Check security code from your bank"
          type="warning"
        />
      </Container>

      <form ref={hiddenForm} action={action} id={id} method="POST" name={id} target="paymentIframe">
        {Object.keys(inputData).map((key) => (
          <input key={key} id={key} name={key} type="hidden" value={inputData[key] || ''} />
        ))}
      </form>

      <Iframe name="paymentIframe" onLoad={onLoadHandler} />

      {frameLoaded && renderPhoneNumber()}
    </Container>
  );

  return (
    <Modal className={cx(styles.modal, 'h-full')} footer={false} isOpen title="Payment authentication">
      {renderContent()}
    </Modal>
  );
};

export default ThreeDSecure;
